import type { AgingStatusBucket } from '../../../../types/insights'
import type { AiReviewView } from '../../../../types/ai'
import type { IngestionRunView } from '../../../../types/ingest'

/**
 * IB-282 View 4 — pure, DB-free/fetch-free mapping helpers (Vitest-friendly by construction, same
 * posture as `dashboards/portfolio/analyticsMappers.ts` / `dashboards/jurisdictions/analyticsMappers.ts`).
 * Every mapper degrades independently and never fabricates a value the backend didn't actually resolve.
 */

/** The four real age buckets, in display order — `"unknown"` is appended ONLY when it has a nonzero count (AC4.1). */
const AGE_BUCKET_ORDER = ['0-7', '8-30', '31-90', '90+'] as const

export interface AgingBarRow {
  ageBucket: string
  count: number
}

/**
 * AC4.1 — collapses the server's `(currentState, ageBucket, count)` rows into ONE total-per-bucket
 * series (the design's "simple 4-bar total" option) — zero-fills the 4 real buckets so an empty
 * bucket renders `0`, never omitted, then appends `"unknown"` ONLY if the backend actually returned a
 * nonzero unknown row (never a fabricated 5th bar when nothing is unknown).
 */
export function buildAgingBarRows(buckets: ReadonlyArray<AgingStatusBucket>): AgingBarRow[] {
  const totals = new Map<string, number>(AGE_BUCKET_ORDER.map((b) => [b, 0]))
  let unknownTotal = 0
  for (const b of buckets) {
    if (b.ageBucket === 'unknown') {
      unknownTotal += b.count
      continue
    }
    totals.set(b.ageBucket, (totals.get(b.ageBucket) ?? 0) + b.count)
  }
  const rows: AgingBarRow[] = AGE_BUCKET_ORDER.map((ageBucket) => ({ ageBucket, count: totals.get(ageBucket) ?? 0 }))
  if (unknownTotal > 0) {
    rows.push({ ageBucket: 'unknown', count: unknownTotal })
  }
  return rows
}

export interface RunVolumeDayRow {
  day: string
  count: number
}

/**
 * AC4.2 — buckets a bounded window of `IngestionRun` rows by UTC calendar day (`createdAt`'s date
 * portion), ascending. An empty `runs` array returns `[]` — the widget itself renders "No activity in
 * this period" for that case (never a fabricated zero-count day).
 */
export function bucketRunsByDay(runs: ReadonlyArray<IngestionRunView>): RunVolumeDayRow[] {
  const counts = new Map<string, number>()
  for (const run of runs) {
    const day = run.createdAt.slice(0, 10) // "YYYY-MM-DD" — UTC ISO instant, stable day key
    counts.set(day, (counts.get(day) ?? 0) + 1)
  }
  return [...counts.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([day, count]) => ({ day, count }))
}

export interface AcceptRateResult {
  accepted: number
  rejected: number
  /** `decision = "edit"` — disclosed separately, deliberately EXCLUDED from both halves of the ratio. */
  edited: number
  /**
   * `null` means "no honest rate can be shown" — `accepted + rejected === 0` (a fabricated `0%`
   * would falsely assert rejections happened, AC4.2's own failure mode). 2-decimal-place rounding
   * (the same `Math.round(x * 10000) / 100` shape `buildStatusCountRows` already uses for a
   * count-derived percentage — exact for integer inputs, never a source of float drift here).
   */
  ratePct: number | null
}

/**
 * AC4.2 — `accepted / (accepted + rejected)`, with `edit` excluded from BOTH the numerator and the
 * denominator (per the DoR's literal formula, design §2) and surfaced separately as `edited`. An
 * empty `reviews` array (or one containing only `edit` dispositions) yields `ratePct: null` — the
 * widget renders "No activity in this period", never `0%`.
 */
export function computeAcceptRate(reviews: ReadonlyArray<AiReviewView>): AcceptRateResult {
  let accepted = 0
  let rejected = 0
  let edited = 0
  for (const r of reviews) {
    if (r.decision === 'accept') accepted += 1
    else if (r.decision === 'reject') rejected += 1
    else if (r.decision === 'edit') edited += 1
  }
  const denominator = accepted + rejected
  const ratePct = denominator > 0 ? Math.round((accepted / denominator) * 10000) / 100 : null
  return { accepted, rejected, edited, ratePct }
}
